import Head from 'next/head';
import type { GetServerSideProps, InferGetStaticPropsType } from 'next';
import { NextPageWithLayout } from '../_app';
import React from 'react';
import { AppSettings } from '@/shared/app-common';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import Layout from '@/layouts';
import { canonicalUrl } from '@/shared/utils';
import { getTdkApi } from '@/lib/service/tdk';
import { fetchLoadingDataWith } from '@/lib/service';
import { page3WithWebApplicationJsonLd } from '@/lib/jsonld';
import { hyphenatedToTitleCase, removeLastPathSegment } from '@/shared/path-helper';

const LandingPageMainContent = dynamic(() => import('@/components/LandingPage'), {
  ssr: true,
});

const WEB_APPLICATION_MAP = {
  story: {
    name: 'Instagram Stories Downloader',
    url: 'https://www.dolphinradar.com/download-from-instagram/story',
    applicationCategory: 'Instagram Downloader',
    description:
      'Save Instagram Stories fast, safely and anonymously with our Instagram Stories Downloader — the best way to keep any IG Story in HD on any device.',
    featureList: [
      'Download stories anonymously',
      'View stories without account',
      'Save stories in HD quality',
      'Download photos and videos',
      'Instagram Video Downloader',
    ],
  },
  highlights: {
    name: 'Instagram Highlights Downloader',
    url: 'https://www.dolphinradar.com/download-from-instagram/highlights',
    applicationCategory: 'Instagram Downloader',
    description:
      'Keep your favorite Instagram highlights organized by saving them in HD with our Highlights Downloader. Access any public highlight anytime and store curated collections on any device.',
    featureList: [
      'Download highlights anonymously',
      'Save all highlights from profile',
      'High quality download',
      'Instagram Downloader',
      'Instagram Story Downloader',
    ],
  },
  post: {
    name: 'Instagram Post Downloader',
    url: 'https://www.dolphinradar.com/download-from-instagram/post',
    applicationCategory: 'Instagram Downloader',
    description:
      'Save Instagram posts in HD — including photos, videos, and carousels — with a tool designed to make downloading public posts quick and effortless across all devices.',
    featureList: [
      'Download posts anonymously',
      'Instagram Video Downloader',
      'Instagram Highlights Downloader',
      'Instagram Story Downloader',
    ],
  },
  video: {
    name: 'Instagram Video Downloader',
    url: 'https://www.dolphinradar.com/download-from-instagram/video',
    applicationCategory: 'Instagram Downloader',
    description:
      'Download Instagram videos online in HD and instantly save any public clip to your device. This Instagram Video Downloader ensures smooth, reliable, and hassle-free downloads every time.',
    featureList: [
      'Instagram Downloader',
      'Instagram Photo Downloader',
      'Instagram Post Downloader',
      'Instagram Story Downloader',
      'Instagram Highlights Downloader',
    ],
  },
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const {
    locale,
    req,
    resolvedUrl,
    params: { id },
  } = context;
  const path = resolvedUrl.split('?')[0];

  const [localeRes, tdkRes, dataRes] = await Promise.all([
    serverSideTranslations(locale, ['common']),
    getTdkApi(path, locale),
    fetchLoadingDataWith(path, locale),
  ]);

  const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};
  const parentPath = removeLastPathSegment(resolvedUrl);
  const siteUrl = locale !== 'en' ? `https://${req.headers.host}/${locale}` : `https://${req.headers.host}`;
  const parentUrl = siteUrl + parentPath;
  const parentTitle = hyphenatedToTitleCase(parentPath);

  const jsonLd = page3WithWebApplicationJsonLd(
    {
      title: hyphenatedToTitleCase(`${id}`),
      desc: tdkRes?.data?.description ?? tempData?.head_Meta_Desc ?? null,
      url: canonicalUrl(req.headers.host, resolvedUrl, locale),
    },
    {
      title: parentTitle,
      url: parentUrl,
    },
    {
      url: siteUrl,
    },
    WEB_APPLICATION_MAP[id.toString().toLowerCase() as keyof typeof WEB_APPLICATION_MAP]
  );
  if (!dataRes?.data) {
    return {
      redirect: {
        destination: `/`,
        permanent: false,
      },
    };
  }

  return {
    props: {
      ...localeRes,
      herf: canonicalUrl(req.headers.host, resolvedUrl, locale),
      locale,
      title: tdkRes?.data?.title ?? tempData?.head_Anonymous_Meta_Title ?? null,
      description: tdkRes?.data?.description ?? tempData?.head_Anonymously_Meta_Desc ?? null,
      contentData: dataRes?.data ?? null,
      jsonLd,
    },
  };
};

const PageContent: NextPageWithLayout<InferGetStaticPropsType<typeof getServerSideProps>> = (props) => {
  return <LandingPageMainContent data={props} />;
};

PageContent.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const desc = page?.props?.description;

  return (
    <Layout
      head={
        <Head>
          <title>{title}</title>
          <meta name="title" content={title} />
          <meta name="description" content={desc} />

          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta property="og:image" content={AppSettings.logoUrl} />
          <meta property="og:image:secure_url" content={AppSettings.logoUrl} />
          <meta property="og:type" content="summary" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={desc} />
          <meta name="twitter:image" content={AppSettings.logoUrl} />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <link rel="canonical" href={page.props?.herf} />
          <link rel="icon" href={AppSettings.icoPath} />
          {page.props?.jsonLd && (
            <script
              async
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(page.props?.jsonLd) }}
            />
          )}
        </Head>
      }
    >
      {page}
    </Layout>
  );
};

export default PageContent;
